// @flow
import update from 'immutability-helper';
import { forkJoin, Observable, of } from 'rxjs';
import {catchError, delay, map, mergeMap, switchMap} from 'rxjs/operators';
import { ofType } from 'redux-observable';
import {postForecastClassicPriceApi, postForecastDiyPriceApi} from '../QuoteStep/api';
import { getWishListsApi, postNewWishListApi, postProductToWishListApi } from './api';
import { uploadImageApi, uploadImageURLApi } from '../api';
import { endpoints } from './api';
import { dataURItoBlob } from '../../../utils/file';
import { setNewAlert } from '../../alert/duck';

import type { HTTPError } from '../../../error';
import type { WishList, WishListItem } from '../../../entities';
import type {Forecast, ForecastCourier, ForecastPricesApiReq, ForecastTier} from '../QuoteStep/api';

type ProductStepAction = {
  +type: string;
  +error?: HTTPError;
  +imageId?: string;
  +nameSlug?: string;
  +userId?: string;
  +wishList?: WishList;
  +wishListItem?: WishListItem;
  +wishListName?: string;
  +wishLists?: WishList[];
  +forecast?: Forecast;
  +forecastRequest?: ForecastPricesApiReq;
};

export type ProductStepState = {
  +isFetchingWishLists?: boolean;
  +wishLists?: WishList[];
  +fetchWishListsError?: HTTPError;
  +isPostingProduct?: boolean;
  +nameSlug?: string;
  +postProductError?: HTTPError;
  +isPostingNewWishList?: boolean;
  +postNewWishListError?: HTTPError;
  +postProductSuccess?: boolean;
  +isLoadingForecast?: boolean;
  +forecastError?: HTTPError;
  +forecast?: Forecast;
};

//Actions
const PRODUCT_STEP_FETCH_WISH_LISTS_REQUEST = 'PRODUCT_STEP_FETCH_WISH_LISTS_REQUEST';
const PRODUCT_STEP_FETCH_WISH_LISTS_SUCCESS = 'PRODUCT_STEP_FETCH_WISH_LISTS_SUCCESS';
const PRODUCT_STEP_FETCH_WISH_LISTS_ERROR = 'PRODUCT_STEP_FETCH_WISH_LISTS_ERROR';

const PRODUCT_STEP_POST_PRODUCT_WISH_LIST_REQUEST = 'PRODUCT_STEP_POST_PRODUCT_WISH_LIST_REQUEST';
const PRODUCT_STEP_POST_PRODUCT_WISH_LIST_IMAGE_SUCCESS = 'PRODUCT_STEP_POST_PRODUCT_WISH_LIST_IMAGE_SUCCESS';
const PRODUCT_STEP_POST_PRODUCT_WISH_LIST_SUCCESS = 'PRODUCT_STEP_POST_PRODUCT_WISH_LIST_SUCCESS';
const PRODUCT_STEP_POST_PRODUCT_WISH_LIST_ERROR = 'PRODUCT_STEP_POST_PRODUCT_WISH_LIST_ERROR';

const PRODUCT_STEP_POST_NEW_WISH_LIST_REQUEST = 'PRODUCT_STEP_POST_NEW_WISH_LIST_REQUEST';
const PRODUCT_STEP_POST_NEW_WISH_LIST_SUCCESS = 'PRODUCT_STEP_POST_NEW_WISH_LIST_SUCCESS';
const PRODUCT_STEP_POST_NEW_WISH_LIST_ERROR = 'PRODUCT_STEP_POST_NEW_WISH_LIST_ERROR';

const PRODUCT_STEP_FORECAST_PRICES_REQUEST = 'PRODUCT_STEP_FORECAST_PRICES_REQUEST';
const PRODUCT_STEP_FORECAST_PRICES_SUCCESS = 'PRODUCT_STEP_FORECAST_PRICES_SUCCESS';
const PRODUCT_STEP_FORECAST_PRICES_ERROR = 'PRODUCT_STEP_FORECAST_PRICES_ERROR';

//Action creators
export function fetchWishLists(userId: string): ProductStepAction {
  return {
    type: PRODUCT_STEP_FETCH_WISH_LISTS_REQUEST,
    userId,
  }
}

export function fetchWishListsSuccess(wishLists: WishList[]): ProductStepAction {
  return {
    type: PRODUCT_STEP_FETCH_WISH_LISTS_SUCCESS,
    wishLists,
  }
}

export function fetchWishListsErrorFn(error: HTTPError): ProductStepAction {
  return {
    type: PRODUCT_STEP_FETCH_WISH_LISTS_ERROR,
    error,
  }
}

export function postProductToWishList(wishListItem: WishListItem, nameSlug: string): ProductStepAction {
  return {
    type: PRODUCT_STEP_POST_PRODUCT_WISH_LIST_REQUEST,
    wishListItem,
    nameSlug,
  }
}

export function postProductToWishListImageSuccess(imageId: string, wishListItem: WishListItem,
                                                  nameSlug: string): ProductStepAction {
  return {
    type: PRODUCT_STEP_POST_PRODUCT_WISH_LIST_IMAGE_SUCCESS,
    imageId,
    wishListItem,
    nameSlug,
  };
}

export function postProductToWishListSuccess(): ProductStepAction {
  return {
    type: PRODUCT_STEP_POST_PRODUCT_WISH_LIST_SUCCESS,
  }
}

export function postProductToWishListErrorFn(error: HTTPError): ProductStepAction {
  return {
    type: PRODUCT_STEP_POST_PRODUCT_WISH_LIST_ERROR,
    error,
  }
}

export function postNewWishList(wishListName: string, wishListItem: WishListItem): ProductStepAction {
  return {
    type: PRODUCT_STEP_POST_NEW_WISH_LIST_REQUEST,
    wishListName,
    wishListItem,
  }
}

export function postNewWishListSuccess(wishList: WishList): ProductStepAction {
  return {
    type: PRODUCT_STEP_POST_NEW_WISH_LIST_SUCCESS,
    wishList,
  }
}

export function postNewWishListErrorFn(error: HTTPError): ProductStepAction {
  return {
    type: PRODUCT_STEP_POST_NEW_WISH_LIST_ERROR,
    error,
  }
}

export function forecastPrices(forecastRequest: ForecastPricesApiReq): ProductStepAction {
  return {
    type: PRODUCT_STEP_FORECAST_PRICES_REQUEST,
    forecastRequest,
  };
}

export function forecastPricesSuccess(forecast: Forecast): ProductStepAction {
  return {
    type: PRODUCT_STEP_FORECAST_PRICES_SUCCESS,
    forecast,
  };
}

export function forecastPricesErrorFn(error: HTTPError): ProductStepAction {
  return {
    type: PRODUCT_STEP_FORECAST_PRICES_ERROR,
    error,
  };
}


export const actions = {
  PRODUCT_STEP_FETCH_WISH_LISTS_REQUEST,
  PRODUCT_STEP_FETCH_WISH_LISTS_SUCCESS,
  PRODUCT_STEP_FETCH_WISH_LISTS_ERROR,
  PRODUCT_STEP_POST_PRODUCT_WISH_LIST_REQUEST,
  PRODUCT_STEP_POST_PRODUCT_WISH_LIST_IMAGE_SUCCESS,
  PRODUCT_STEP_POST_PRODUCT_WISH_LIST_SUCCESS,
  PRODUCT_STEP_POST_PRODUCT_WISH_LIST_ERROR,
  PRODUCT_STEP_POST_NEW_WISH_LIST_REQUEST,
  PRODUCT_STEP_POST_NEW_WISH_LIST_SUCCESS,
  PRODUCT_STEP_POST_NEW_WISH_LIST_ERROR,
  PRODUCT_STEP_FORECAST_PRICES_REQUEST,
  PRODUCT_STEP_FORECAST_PRICES_SUCCESS,
  PRODUCT_STEP_FORECAST_PRICES_ERROR,
  fetchWishLists,
  fetchWishListsSuccess,
  fetchWishListsErrorFn,
  postProductToWishList,
  postProductToWishListImageSuccess,
  postProductToWishListSuccess,
  postProductToWishListErrorFn,
  postNewWishList,
  postNewWishListSuccess,
  postNewWishListErrorFn,
  forecastPrices,
  forecastPricesSuccess,
  forecastPricesErrorFn,
};

export const initialState: ProductStepState = {};

// Reducer
export default function reducer(state: ProductStepState = initialState,
                                action: ProductStepAction): ProductStepState {
  switch (action.type) {
    case PRODUCT_STEP_FETCH_WISH_LISTS_REQUEST:
      return update(state, {
        isFetchingWishLists: { $set: true },
      });
    case PRODUCT_STEP_FETCH_WISH_LISTS_SUCCESS:
      return update(state, {
        $unset: ['isFetchingWishLists'],
        wishLists: { $set: action.wishLists },
      });
    case PRODUCT_STEP_FETCH_WISH_LISTS_ERROR:
      return update(state, {
        fetchWishListsError: { $set: action.error },
      });
    case PRODUCT_STEP_POST_PRODUCT_WISH_LIST_REQUEST:
      return update(state, {
        isPostingProduct: { $set: true },
        $unset: ['postProductError', 'postProductSuccess'],
      });
    case PRODUCT_STEP_POST_PRODUCT_WISH_LIST_SUCCESS:
      return update(state, {
        $unset: ['isPostingProduct', 'postProductError'],
        postProductSuccess: { $set: true },
      });
    case PRODUCT_STEP_POST_PRODUCT_WISH_LIST_ERROR:
      return update(state, {
        $unset: ['isPostingProduct'],
        postProductError: { $set: action.error },
      });
    case PRODUCT_STEP_POST_NEW_WISH_LIST_REQUEST:
      return update(state, {
        $unset: ['postNewWishListError'],
        isPostingNewWishList: { $set: true },
      });
    case PRODUCT_STEP_POST_NEW_WISH_LIST_SUCCESS:
      if (!state.wishLists) return state;
      return update(state, {
        $unset: ['isPostingNewWishList'],
        wishLists: { $set: [...state.wishLists, action.wishList] },
      });
    case PRODUCT_STEP_POST_NEW_WISH_LIST_ERROR:
      return update(state, {
        $unset: ['isPostingNewWishList'],
        postNewWishListError: { $set: action.error },
      });
    case PRODUCT_STEP_FORECAST_PRICES_REQUEST:
      return update(state, {
        isLoadingForecast: { $set: true },
        $unset: ['forecastError'],
        // $unset: ['forecast'],
        // $unset: ['selectedIndex'],
      });
    case PRODUCT_STEP_FORECAST_PRICES_SUCCESS:
      return update(state, {
        forecast: { $set: action.forecast },
        // selectedIndex: { $set: Math.max((action.forecast || []).length - 1, 0) },
        $unset: ['isLoadingForecast'],
      });
    case PRODUCT_STEP_FORECAST_PRICES_ERROR:
      return update(state, {
        $unset: ['isLoadingForecast'],
        // selectedIndex: { $set: Math.max((state.forecast || []).length - 1, 0) },
        forecastError: { $set: action.error },
      });
    default:
      return state;
  }
}

export function newOrderProductFetchWishListsEpic(action$: Observable<ProductStepAction>): Observable<ProductStepAction> {
  return action$.pipe(
    ofType(PRODUCT_STEP_FETCH_WISH_LISTS_REQUEST),
    mergeMap(({ userId }) => getWishListsApi(undefined, {
      route: endpoints.GET_WISH_LISTS.replace(':userId', userId)
    }).pipe(
      map((wishLists: WishList[]) => fetchWishListsSuccess(wishLists)),
      catchError((e: HTTPError) => of(fetchWishListsErrorFn(e))),
    )),
  );
}

export function newOrderProductPostProductToWishListImageEpic(action$: Observable<ProductStepAction>): Observable<ProductStepAction> {
  return action$.pipe(
    ofType(PRODUCT_STEP_POST_PRODUCT_WISH_LIST_REQUEST),
    mergeMap(({ wishListItem, nameSlug }) => {
      const { image, imageURI } = wishListItem;
      let obs = of({});
      if (image) {
        obs = uploadImageURLApi({
          url: image,
          resourceGroup: 'PRODUCT',
        });
      } else if (imageURI) {
        const fd = new FormData();
        fd.append('file', dataURItoBlob(imageURI));
        fd.append('resourceGroup', 'PRODUCT');
        obs = uploadImageApi(fd, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });
      }
      return obs.pipe(
        mergeMap((imageResponse: { id: string }) =>
          of(postProductToWishListImageSuccess(imageResponse.id, wishListItem, nameSlug))
        ),
        catchError((e: HTTPError) => {
          if (Math.round(e.code / 100) === 5) {
            return of(postProductToWishListErrorFn(e),
              setNewAlert('new-order', 'error', undefined, 'httpErrors.post.500'),
            );
          } else if (e.code === 400) {
            return of(postProductToWishListErrorFn(e),
              setNewAlert('new-order', 'error', undefined, 'httpErrors.post.400'),
            );
          }
          return of(postProductToWishListErrorFn(e));
        }));
    }),
  )
}

export function newOrderProductPostProductToWishListEpic(action$: Observable<ProductStepAction>): Observable<ProductStepAction> {
  return action$.pipe(
    ofType(PRODUCT_STEP_POST_PRODUCT_WISH_LIST_IMAGE_SUCCESS),
    mergeMap(({ imageId, wishListItem, nameSlug }) => {
      if (wishListItem) {
        wishListItem.image = imageId;
        delete wishListItem.imageURI;
      }
      return postProductToWishListApi(wishListItem, {
        route: endpoints.POST_PRODUCT_WISH_LIST.replace(':nameSlug', nameSlug),
      }).pipe(
        map(() => postProductToWishListSuccess()),
        catchError((e: HTTPError) => {
          if (Math.round(e.code / 100) === 5) {
            return of(postProductToWishListErrorFn(e),
              setNewAlert('new-order', 'error', undefined, 'httpErrors.post.500'),
            );
          } else if (e.code === 400) {
            return of(postProductToWishListErrorFn(e),
              setNewAlert('new-order', 'error', undefined, 'httpErrors.post.400'),
            );
          }
          return of(postProductToWishListErrorFn(e));
        }));
    }),
  );
}

export function newOrderProductPostNewWishListEpic(action$: Observable<ProductStepAction>): Observable<ProductStepAction> {
  return action$.pipe(
    ofType(PRODUCT_STEP_POST_NEW_WISH_LIST_REQUEST),
    mergeMap(({ wishListName, wishListItem }) => postNewWishListApi(wishListName).pipe(
      mergeMap((wishList: WishList) => of(postNewWishListSuccess(wishList), postProductToWishList(wishListItem, wishList.nameSlug))),
      catchError((e: HTTPError) => {
        if (Math.round(e.code / 100) === 5) {
          return of(postNewWishListErrorFn(e),
            setNewAlert('new-order', 'error', undefined, 'httpErrors.post.500'),
          );
        } else if (e.code === 400) {
          return of(postNewWishListErrorFn(e),
            setNewAlert('new-order', 'error', undefined, 'httpErrors.post.400'),
          );
        }
        return of(postNewWishListErrorFn(e))
      }),
    )),
  );
}

export function newOrderProductQuoteForecastPricesEpic(action$: Observable<ProductStepAction>): Observable<ProductStepAction> {
  return action$.pipe(
    ofType(PRODUCT_STEP_FORECAST_PRICES_REQUEST),
    switchMap(({ forecastRequest }: { forecastRequest: ForecastPricesApiReq }) => {
      const req = {
        ...forecastRequest,
      };
      const { bagId, items } = forecastRequest;
      
      // NOTE: Control errors pls
      return forkJoin([
        postForecastDiyPriceApi(req), 
        postForecastClassicPriceApi(req)
      ]).pipe(
        delay(3000),
        mergeMap(([diy, classic]: [ForecastCourier[], ForecastTier[]]) => {
          const forecast = {
            DIY: diy,
            CLASSIC: classic,
          };
          return of(forecastPricesSuccess(forecast));
        }),
        catchError((e: HTTPError) => of(forecastPricesErrorFn(e))),
      );
    })
  );
}



// WEBPACK FOOTER //
// ./src/modules/new-order/ProductStep/duck.js